import { format, formatDistance } from 'date-fns'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import type { RunDetail } from '@/api/types'

interface OverviewTabProps {
  run: RunDetail
}

interface MetadataRowProps {
  label: string
  value: React.ReactNode
}

function MetadataRow({ label, value }: MetadataRowProps) {
  return (
    <div className="flex justify-between py-2 border-b last:border-0">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium text-right">{value || '-'}</span>
    </div>
  )
}

/**
 * Format duration between two dates.
 */
function formatDuration(start: string, end?: string): string {
  const startDate = new Date(start)
  const endDate = end ? new Date(end) : new Date()
  return formatDistance(startDate, endDate, { includeSeconds: true })
}

export function OverviewTab({ run }: OverviewTabProps) {
  // Extract metadata from run
  const metadata = run.metadata || {}
  const tokenUsage = metadata.tokenUsage as { prompt?: number; completion?: number; total?: number } | undefined

  return (
    <div className="grid gap-6 md:grid-cols-2">
      {/* Run Information */}
      <Card>
        <CardHeader>
          <CardTitle>Run Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-1">
          <MetadataRow label="File ID" value={run.fileId} />
          <MetadataRow
            label="Original Name"
            value={run.originalName || run.fileName}
          />
          <MetadataRow label="Status" value={run.status} />
          <MetadataRow label="Workflow" value={run.workflow} />
          {run.error && (
            <MetadataRow
              label="Error"
              value={
                <span className="text-destructive text-sm">{run.error}</span>
              }
            />
          )}
        </CardContent>
      </Card>

      {/* Phenotype */}
      <Card>
        <CardHeader>
          <CardTitle>Phenotype Detection</CardTitle>
        </CardHeader>
        <CardContent className="space-y-1">
          <MetadataRow label="Detected Phenotype" value={run.phenotype} />
          <MetadataRow
            label="Confidence"
            value={
              run.phenotypeConfidence !== undefined
                ? `${(run.phenotypeConfidence * 100).toFixed(1)}%`
                : undefined
            }
          />
        </CardContent>
      </Card>

      {/* Timing */}
      <Card>
        <CardHeader>
          <CardTitle>Timing</CardTitle>
        </CardHeader>
        <CardContent className="space-y-1">
          <MetadataRow
            label="Created"
            value={
              run.createdAt
                ? format(new Date(run.createdAt), 'PPpp')
                : undefined
            }
          />
          <MetadataRow
            label="Last Updated"
            value={
              run.updatedAt
                ? format(new Date(run.updatedAt), 'PPpp')
                : undefined
            }
          />
          <MetadataRow
            label="Duration"
            value={
              run.createdAt && (run.status === 'completed' || run.status === 'failed')
                ? formatDuration(run.createdAt, run.updatedAt)
                : run.status === 'processing'
                ? 'In progress...'
                : undefined
            }
          />
        </CardContent>
      </Card>

      {/* Token Usage */}
      <Card>
        <CardHeader>
          <CardTitle>Token Usage</CardTitle>
        </CardHeader>
        <CardContent className="space-y-1">
          <MetadataRow
            label="Prompt Tokens"
            value={tokenUsage?.prompt?.toLocaleString()}
          />
          <MetadataRow
            label="Completion Tokens"
            value={tokenUsage?.completion?.toLocaleString()}
          />
          <MetadataRow
            label="Total Tokens"
            value={tokenUsage?.total?.toLocaleString()}
          />
          {!tokenUsage && (
            <p className="text-sm text-muted-foreground py-2">
              Token usage data not available for this run.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
